/**
 * Normalizes API/axios-style errors into a stable shape for use-case catch blocks.
 * Extracts FastAPI validation messages from either a root error array or a
 * `detail` array, then falls back to response message/title and error.message.
 */
export interface ParsedApiError {
  message: string;
  status?: number;
  data?: unknown;
}

type DetailItem = { msg?: unknown; message?: unknown };

type MaybeApiError = {
  message?: string;
  status?: number;
  response?: {
    status?: number;
    data?: unknown;
  };
};

export function parseAxiosApiError(
  error: unknown,
  fallbackMessage: string,
): ParsedApiError {
  const err = error as MaybeApiError;
  const status = err?.response?.status ?? err?.status;
  const responseData = err?.response?.data;
  const responseObject =
    responseData !== null && typeof responseData === "object"
      ? (responseData as {
          detail?: unknown;
          message?: unknown;
          title?: unknown;
        })
      : undefined;
  const detail = responseObject?.detail;

  let message = fallbackMessage;
  let data: unknown = responseData;

  // String-guards each candidate so a non-string field (an object-shaped
  // validator entry sometimes nested inside FastAPI detail, e.g.
  // `detail: [{ msg: { type: "...", ... } }]`) can't sneak through. Without
  // this, the toast template literal renders "[object Object]". Falling back
  // to the parent candidate / fallback message beats "shows the user raw JS".
  const stringOrUndefined = (v: unknown): string | undefined =>
    typeof v === "string" && v.length > 0 ? v : undefined;

  const validationErrors = Array.isArray(responseData)
    ? responseData
    : Array.isArray(detail)
    ? detail
    : undefined;

  if (validationErrors?.length) {
    const first = validationErrors[0] as DetailItem | undefined;
    message =
      stringOrUndefined(first?.msg) ??
      stringOrUndefined(first?.message) ??
      message;
    data = validationErrors[0];
  } else if (typeof detail === "string" && detail.trim()) {
    message = detail;
    data = detail;
  } else {
    message =
      stringOrUndefined(responseObject?.message) ??
      stringOrUndefined(responseObject?.title) ??
      stringOrUndefined(err?.message) ??
      message;
  }

  return { message, status, data };
}
